// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

// material ui
import { Box, Button, Card, CardHeader, Stack } from '@mui/material';

// api
import { useGetAccountByIdQuery } from '@/api/recalc/endpoints/accountsApiSlice';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import ServiceTabs from '@/features/service-manager/components/ServiceTabs';

export const Route = createFileRoute(
  '/$accountId/_admin/service-manager/$managedAccountId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('service-manager');
  const location = useLocation();
  const navigate = useNavigate();
  const { accountId, managedAccountId } = useParams({
    from: '/$accountId/_admin/service-manager/$managedAccountId',
  });

  const { data: account } = useGetAccountByIdQuery(managedAccountId);
  const scopeToAccountLabel = t('accountHeader.scopeToAccount', {
    defaultValue: 'Scope to Account',
  });
  const manageUsersLabel = t('overview.userAccess.manageUsers');

  const handleScopeToAccount = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    if (!managedAccountId || pathSegments.length === 0) return;

    const nextPath = `/${[managedAccountId, ...pathSegments.slice(1)].join('/')}`;
    void navigate({ to: nextPath });
  };

  const handleManageUsers = () => {
    if (!managedAccountId) return;
    void navigate({ to: `/${managedAccountId}/account/users` });
  };

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <PageHeader title={t('title')} to="/$accountId/service-manager" />
      <Stack spacing={2}>
        <Card>
          <CardHeader
            title={account?.Name || t('accountHeader.notAvailable')}
            subheader={
              account?.Address || t('accountHeader.addressNotAvailable')
            }
            action={
              <Box display="flex" gap={1}>
                {accountId !== managedAccountId && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleScopeToAccount}
                  >
                    {scopeToAccountLabel}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleManageUsers}
                >
                  {manageUsersLabel}
                </Button>
              </Box>
            }
            slotProps={{
              title: { variant: 'h6', fontWeight: 600 }
            }}
          />
        </Card>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', lg: 320 },
              flexShrink: 0,
            }}
          >
            <ServiceTabs
              account={account}
              orientation="vertical"
              accountId={accountId}
              selectedAccountId={managedAccountId}
              routePrefix="service-manager"
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Outlet />
          </Box>
        </Box>
      </Stack>
    </PageContainer>
  );
}
